import React, { useState, useEffect, useRef } from 'react';
import { Megaphone, X, GripVertical } from 'lucide-react';
import { getLatestAnnouncement } from '../../../lib/announcementService';

const AnnouncementWidget = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const widgetRef = useRef(null);

  // Fetch announcement data from Firebase
  useEffect(() => {
    let isMounted = true;

    const loadAnnouncement = async () => {
      try {
        setLoading(true);
        const latestAnnouncement = await getLatestAnnouncement();
        console.log('latestAnnouncement',latestAnnouncement);
        // Only update state if component is still mounted
        if (isMounted) {
          if (latestAnnouncement) {
            setAnnouncement(latestAnnouncement);
          } else {
            setAnnouncement(null); // Explicitly set null if nothing returned
          }
        }
      } catch (error) {
        console.error('Error loading announcement:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadAnnouncement();

    return () => { isMounted = false; };
  }, []);

  // Initialize position to bottom-right
  useEffect(() => {
    const positionBottomRight = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      if (viewportWidth < 640) {
        setPosition({
          x: viewportWidth - 70, 
          y: viewportHeight - 90
        });
      } else {
        setPosition({
          x: viewportWidth - 90, 
          y: viewportHeight - 110 
        });
      }
    };

    if (position?.x === null) {
      positionBottomRight();
    }

    window.addEventListener('resize', positionBottomRight);
    return () => window.removeEventListener('resize', positionBottomRight);
  }, [position?.x]);

  // Handle Dragging Logic
  const handleStart = (clientX, clientY) => {
    if (!isExpanded) {
      setIsDragging(true);
      setHasDragged(false);
      const rect = widgetRef?.current?.getBoundingClientRect();
      setDragOffset({
        x: clientX - rect?.left,
        y: clientY - rect?.top
      });
    }
  };

  const handleMouseDown = (e) => {
    if (e?.target?.closest('.drag-handle')) {
      handleStart(e.clientX, e.clientY);
    }
  };

  const handleTouchStart = (e) => {
    if (e?.target?.closest('.drag-handle')) {
      const touch = e?.touches?.[0];
      handleStart(touch?.clientX, touch?.clientY);
    }
  };

  useEffect(() => {
    const handleMove = (clientX, clientY) => {
      if (isDragging && widgetRef?.current) {
        setHasDragged(true);
        const newX = clientX - dragOffset?.x;
        const newY = clientY - dragOffset?.y;
        
        const widgetWidth = widgetRef?.current?.offsetWidth;
        const widgetHeight = widgetRef?.current?.offsetHeight;
        const maxX = window.innerWidth - widgetWidth;
        const maxY = window.innerHeight - widgetHeight;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };

    const handleEnd = () => setIsDragging(false);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, dragOffset]);

  const handleDoubleClick = () => {
    if (!hasDragged && !isExpanded) {
      setIsExpanded(true);
    }
  };

  // If loading, or no active announcement, hide widget completely
  if (loading || !announcement) {
    return null;
  }

  return (
    <>
      {/* Floating Widget Button */}
      {!isExpanded && (
        <div
          ref={widgetRef}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onDoubleClick={handleDoubleClick}
          style={{
            left: position?.x !== null ? `${position?.x}px` : '50%',
            top: position?.y !== null ? `${position?.y}px` : '50%',
            transform: position?.x === null ? 'translate(-50%, -50%)' : 'none',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
          className="fixed z-30 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl shadow-2xl transition-all hover:scale-105 group"
        >
          <div className="drag-handle flex items-center gap-2 px-4 py-3 sm:px-5 sm:py-4">
            <GripVertical className="h-4 w-4 sm:h-5 sm:w-5 opacity-70" />
            <Megaphone className="h-5 w-5 sm:h-6 sm:w-6" />
            <span className="text-xs sm:text-sm font-semibold max-w-[120px] sm:max-w-[180px] truncate">
              {announcement.title}
            </span>
          </div>
          {/* Unread Indicator */}
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center animate-pulse shadow-sm">
            1
          </div>
        </div>
      )}

      {/* Expanded Modal */}
      {isExpanded && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-5xl bg-gradient-to-br from-blue-50 to-green-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden border-4 border-[var(--color-accent)]">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-4 sm:px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Megaphone className="h-5 w-5 sm:h-6 sm:w-6" />
                <h3 className="text-base sm:text-lg font-bold">Park Announcement</h3>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-white hover:bg-blue-700 rounded-full p-1 transition-colors"
                aria-label="Close announcement"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>


            {/* Content Scroll Area */}
            <div className="px-4 sm:px-6 py-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="text-center mb-6">
                {/* <div className="inline-block bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-4 rounded-xl shadow-lg mb-4">
                  <h2 className="text-3xl sm:text-5xl font-black tracking-tight">{announcement?.title}</h2>
                </div> */}
                <h4 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-2">
                  {announcement?.title}
                </h4>
              </div>
              <div 
                className="prose prose-sm sm:prose-base max-w-none text-gray-700 dark:text-gray-300"
                dangerouslySetInnerHTML={{ __html: announcement?.description }}
              />
              <div className="mt-6 pt-4 border-t border-gray-300 dark:border-gray-600 text-center">
                
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Posted: {announcement.createdAt ? new Date(announcement.createdAt).toLocaleDateString() : 'Recently'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For updates, visit our <a href="https://facebook.com/ppundergroundriver" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">Facebook Page</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnnouncementWidget;